/**
 * The article's own h2/h3 headings, read from the body HTML on the server and
 * the client alike (plain string work, no DOM), so the contents panel and the
 * heading anchors are in the SSR markup and identical after hydration.
 *
 * Blog bodies are stored as HTML (see blog.api.ts). Their headings carry no
 * ids today, so each one gets a slug-like id derived from its own text. An id
 * the editor already wrote is kept as it is: existing anchors never move.
 */

export interface ArticleHeading {
  readonly id: string;
  readonly text: string;
  readonly level: 2 | 3;
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  ndash: "–",
  mdash: "\u2014",
  hellip: "…",
};

function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[body.toLowerCase()] ?? whole;
  });
}

/** Visible text of a heading's inner HTML. */
export function headingText(innerHtml: string): string {
  return decodeEntities(innerHtml.replace(/<[^>]*>/g, "")).replace(/\s+/g, " ").trim();
}

/**
 * "2. Gayza at Riad Fès" -> "2-gayza-at-riad-fes". Letters of any script are
 * kept (an Arabic heading keeps its Arabic id), accents are folded, and the
 * rest collapses to single hyphens. Stable: the same text always gives the
 * same id, so a shared link to a section keeps working.
 */
export function slugifyHeading(text: string): string {
  const slug = text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
  return slug || "section";
}

const HEADING_RE = /<h([23])(\s[^>]*)?>([\s\S]*?)<\/h\1>/gi;
const ID_ATTR_RE = /\sid\s*=\s*["']([^"']+)["']/i;

/**
 * Gives every h2/h3 an id and returns the headings in document order.
 * Duplicate texts get "-2", "-3"; an id already in the HTML is reused and
 * reserved so a generated id never collides with it.
 */
export function withHeadingIds(html: string | undefined): { html: string; headings: ArticleHeading[] } {
  if (!html) return { html: "", headings: [] };

  // Reserve every id already present so generated ones cannot shadow them.
  const used = new Set<string>();
  for (const m of html.matchAll(/\sid\s*=\s*["']([^"']+)["']/gi)) used.add(m[1]!);

  const headings: ArticleHeading[] = [];
  const out = html.replace(HEADING_RE, (whole, lvl: string, attrs: string | undefined, inner: string) => {
    const text = headingText(inner);
    if (!text) return whole;
    const level = (lvl === "2" ? 2 : 3) as 2 | 3;
    const existing = attrs?.match(ID_ATTR_RE)?.[1];
    if (existing) {
      headings.push({ id: existing, text, level });
      return whole;
    }
    const base = slugifyHeading(text);
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
    used.add(id);
    headings.push({ id, text, level });
    return `<h${lvl} id="${id}"${attrs ?? ""}>${inner}</h${lvl}>`;
  });
  return { html: out, headings };
}

/**
 * The rows the contents panel lists. h2s are the article's sections; h3s only
 * join them when the article has too few h2s to navigate by (a listicle with
 * 20 venue h3s under 4 h2s lists the 4).
 */
export function contentsEntries(headings: readonly ArticleHeading[]): ArticleHeading[] {
  const h2 = headings.filter((h) => h.level === 2);
  return h2.length >= 3 ? h2 : [...headings];
}

/**
 * The section being read, from one IntersectionObserver batch.
 *
 * The observer watches a reading band below the sticky header. A heading
 * entering the band becomes current; a heading leaving it through the bottom
 * (the reader scrolled back up past it) hands over to the one before. Both
 * sides compare within the observer's own coordinates, so the site's 0.9 body
 * zoom cannot skew it.
 */
export function nextActiveIndex(
  prev: number,
  entries: ReadonlyArray<{ index: number; isIntersecting: boolean; top: number; rootBottom: number }>,
): number {
  let next = prev;
  const sorted = [...entries].sort((a, b) => a.index - b.index);
  for (const e of sorted) {
    if (e.isIntersecting) {
      next = e.index;
    } else if (e.top >= e.rootBottom && e.index <= next) {
      next = e.index - 1;
    }
  }
  return Math.max(-1, next);
}

/** 0..1 through the article: top edge at the header, bottom edge at the viewport bottom. */
export function readingProgress(articleTop: number, articleHeight: number, viewportHeight: number, offset = 0): number {
  const travel = articleHeight - viewportHeight + offset;
  if (travel <= 0) return articleTop <= offset ? 1 : 0;
  const p = (offset - articleTop) / travel;
  return p < 0 ? 0 : p > 1 ? 1 : p;
}
